import { 
  COMMITMENT_STATUS_COLORS, 
  COMMITMENT_STATUS_LABELS,
  INVESTMENT_PERCENTAGE_OPTIONS 
} from '../constants/investmentStatus';

/**
 * Format currency values
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format percentage values
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format date to readable string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date to readable string with time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Get status color class
 */
export const getStatusColor = (status) => {
  return COMMITMENT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800 border-gray-200';
};

/**
 * Get formatted status label
 */
export const getStatusLabel = (status) => {
  return COMMITMENT_STATUS_LABELS[status] || status;
};

/**
 * Calculate investment amount from percentage
 */
export const calculateInvestmentAmount = (totalBase, percentage) => {
  return (totalBase * percentage) / 100;
};

/**
 * Calculate expected return (guaranteed return + profit share)
 */
export const calculateExpectedReturn = (investmentAmount, profitShare, guaranteedReturnPct) => {
  const guaranteedReturn = (investmentAmount * guaranteedReturnPct) / 100;
  return investmentAmount + guaranteedReturn + profitShare;
};

/**
 * Calculate ROI percentage
 */
export const calculateROI = (investmentAmount, totalReturn) => {
  if (!investmentAmount || investmentAmount === 0) return 0;
  return ((totalReturn - investmentAmount) / investmentAmount) * 100;
};

/**
 * Get available percentage options for investment
 * Filters out percentages that would exceed the remaining available
 */
export const getAvailablePercentageOptions = (remainingPercentage) => {
  return INVESTMENT_PERCENTAGE_OPTIONS.filter(pct => pct <= remainingPercentage);
};

/**
 * Calculate funding progress color
 */
export const getFundingProgressColor = (percentage) => {
  if (percentage >= 100) return 'bg-green-500';
  if (percentage >= 75) return 'bg-blue-500';
  if (percentage >= 50) return 'bg-yellow-500';
  if (percentage >= 25) return 'bg-orange-500';
  return 'bg-red-500';
};

/**
 * Get property location string
 */
export const getPropertyLocation = (opportunity) => {
  const parts = [];
  if (opportunity.property_city) parts.push(opportunity.property_city);
  if (opportunity.property_state) parts.push(opportunity.property_state);
  return parts.join(', ') || 'Location not specified';
};

/**
 * Calculate profit share for a given investment percentage
 */
export const calculateProfitShare = (investorProfitTotal, investmentPercentage) => {
  return (investorProfitTotal * investmentPercentage) / 100;
};

/**
 * Validate commitment percentage
 */
export const isValidCommitmentPercentage = (percentage) => {
  return percentage >= 5 && percentage <= 100 && percentage % 5 === 0;
};
